import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Wallet as WalletIcon, Banknote, Smartphone } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { api, formatApiError, formatINR } from "../lib/api";
import { toast } from "sonner";
import { ProductImage } from "../components/ProductImage";
import { StoreClosedNotice } from "../components/StoreClosedNotice";
import { useAuth } from "../contexts/AuthContext";
import { useDeliveryLocation } from "../contexts/DeliveryLocationContext";

function extractPincode(value) {
  const matches = String(value || "").match(/\b\d{6}\b/g);
  return matches?.at(-1) || "";
}

function usableCoordinate(value) {
  return value !== null && value !== undefined && Number.isFinite(Number(value));
}


function razorpayContact(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 10 ? `+91${digits}` : value;
}

function distanceInKm(first, second) {
  const radians = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const latDifference = radians(second.lat - first.lat);
  const lngDifference = radians(second.lng - first.lng);
  const area =
    Math.sin(latDifference / 2) ** 2 +
    Math.cos(radians(first.lat)) * Math.cos(radians(second.lat)) * Math.sin(lngDifference / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(area), Math.sqrt(1 - area));
}

function deliveryCoverage(areas, address, coordinates) {
  const pin = extractPincode(address);
  const hasCoordinates = usableCoordinate(coordinates?.lat) && usableCoordinate(coordinates?.lng);
  const coordinate = hasCoordinates
    ? { lat: Number(coordinates.lat), lng: Number(coordinates.lng) }
    : null;
  const activeRules = areas.filter((area) => {
    const pins = [area.pincode, ...(area.pincodes || [])].filter(Boolean);
    return pins.length > 0 || Boolean(area.radius_km);
  });
  const radiusRules = activeRules.filter(
    (area) =>
      usableCoordinate(area.center_latitude) &&
      usableCoordinate(area.center_longitude) &&
      Number(area.radius_km) > 0,
  );
  const matchedAreas = activeRules.filter((area) => {
    const pins = [area.pincode, ...(area.pincodes || [])].filter(Boolean);
    const pinMatches = Boolean(pin) && pins.includes(pin);
    const hasRadius =
      usableCoordinate(area.center_latitude) &&
      usableCoordinate(area.center_longitude) &&
      Number(area.radius_km) > 0;
    const radiusMatches =
      coordinate &&
      hasRadius &&
      distanceInKm(coordinate, {
        lat: Number(area.center_latitude),
        lng: Number(area.center_longitude),
      }) <= Number(area.radius_km);
    return coordinate && radiusRules.length > 0 ? radiusMatches : pinMatches;
  });
  return {
    pin,
    hasRules: activeRules.length > 0,
    hasLocation: Boolean(pin) || Boolean(coordinate),
    matches: matchedAreas,
  };
}

// Hard whitelist: any Mirganj / 841438 address is always serviceable.
const HOME_ZONE = /mirganj|841438/i;

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { location, setLocation } = useDeliveryLocation();
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [pay, setPay] = useState("COD");
  const [busy, setBusy] = useState(false);
  const [coords, setCoords] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [serviceAreas, setServiceAreas] = useState([]);
  const [showOutOfZoneModal, setShowOutOfZoneModal] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [info, setInfo] = useState({ phone: "", upi: "", free_delivery_above: 599, delivery_fee: 20 });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/business-info").then((r) => setInfo(r.data));
    api.get("/account/addresses").then((r) => setSavedAddresses(r.data)).catch(() => setSavedAddresses([]));
    api.get("/service-areas").then((r) => setServiceAreas(r.data)).catch(() => setServiceAreas([]));
    api.get("/account/wallet").then((r) => setWalletBalance(r.data.balance || 0)).catch(() => setWalletBalance(0));
  }, []);

  useEffect(() => {
    if (!location?.lat || !location?.lng) return;
    setCoords({ lat: location.lat, lng: location.lng });
    if (!address && location.address && location.address !== "Current location") {
      setAddress(location.address);
    }
  }, [address, location]);

  const freeAbove = Number(info.free_delivery_above) || 599;
  const feeAmt = Number(info.delivery_fee) || 20;
  const discount = coupon?.discount || 0;
  const afterDiscount = Math.max(0, subtotal - discount);
  const deliveryFee = afterDiscount >= freeAbove || afterDiscount === 0 ? 0 : feeAmt;
  const total = afterDiscount + deliveryFee + Number(tip || 0);
  const coverage = deliveryCoverage(serviceAreas, address, coords);
  const deliveryPin = coverage.pin;
  const inHomeZone = HOME_ZONE.test(address);
  const isOutOfServiceArea =
    !inHomeZone && coverage.hasRules && coverage.hasLocation && coverage.matches.length === 0;
  const isInServiceArea = inHomeZone || coverage.matches.length > 0;
  const walletShort = walletBalance < total;
  const storeClosed = info.store_status?.is_open === false;

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/coupons/apply", { code: couponCode, subtotal });
      setCoupon(data);
      toast.success(`Coupon applied · saved ${formatINR(data.discount)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(formatApiError(e));
    }
  };

  const placeOrder = async (paymentId, paymentOrderId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id || null, qty: i.qty })),
        address,
        phone,
        payment_method: pay,
        notes,
        tip: Number(tip || 0),
        area: info?.city || "Mirganj",
        latitude: coords?.lat || null,
        longitude: coords?.lng || null,
        coupon_code: coupon?.code || null,
        payment_id: paymentId || null,
        payment_order_id: paymentOrderId || null,
      });
      clear();
      toast.success(pay === "COD" ? "Order placed successfully!" : "Payment Successful ✅");
      nav(`/orders/${data.id}`);
    } catch (e) {
      if (e.response?.status === 422) setShowOutOfZoneModal(true);
      if (e.response?.data?.detail?.code === "store_closed") {
        setInfo((current) => ({ ...current, store_status: e.response.data.detail.store_status }));
      }
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startRazorpayCheckout = async () => {
    if (storeClosed) {
      toast.error(info.store_status?.message || "Store is temporarily closed");
      return;
    }
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
    if (!razorpayKey || !window.Razorpay) {
      toast.error("Razorpay checkout is unavailable. Please try again shortly.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/create-order", {
        amount: Math.round(total * 100),
        currency: "INR",
        receipt: `qdn_${Date.now()}`,
      });
      const checkout = new window.Razorpay({
        key: razorpayKey,
        amount: data.amount,
        currency: data.currency,
        name: "Quick Door Now",
        description: "Delivery order payment",
        order_id: data.order_id,
        config: {
          display: {
            blocks: {
              upi: {
                name: "Pay via UPI",
                instruments: [{ method: "upi" }],
              },
            },
            sequence: ["block.upi"],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
        prefill: {
          name: user?.name || "Quick Door Now Customer",
          email: user?.email || "",
          contact: razorpayContact(phone),
        },
        theme: { color: "#FF5200" },
        handler: async (response) => {
          try {
            if (response.razorpay_order_id !== data.order_id) {
              throw new Error("Razorpay returned an unexpected payment order");
            }
            await api.post("/verify-payment", response);
            await placeOrder(response.razorpay_payment_id, response.razorpay_order_id);
          } catch (error) {
            setBusy(false);
            toast.error(formatApiError(error));
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(false);
            toast.info("Payment cancelled. Your cart is unchanged.");
          },
        },
      });
      checkout.on("payment.failed", (response) => {
        setBusy(false);
        toast.error(response.error?.description || "Payment failed. Please try again.");
      });
      checkout.open();
    } catch (error) {
      setBusy(false);
      toast.error(formatApiError(error));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (storeClosed) {
      toast.error(info.store_status?.message || "Store is temporarily closed");
      return;
    }
    if (!address.trim()) return toast.error("Please add your delivery address");
    if (isOutOfServiceArea) {
      setShowOutOfZoneModal(true);
      return;
    }
    if (pay === "WALLET" && walletShort) {
      toast.error("Insufficient wallet balance. Add money or choose another method.");
      return;
    }
    if (pay === "ONLINE") {
      startRazorpayCheckout();
      return;
    }
    placeOrder();
  };

  const saveUnserviceableLead = async (event) => {
    event.preventDefault();
    setLeadBusy(true);
    try {
      await api.post("/unserviceable-leads", {
        name: user?.name || "Customer",
        phone,
        address,
        latitude: coords?.lat || null,
        longitude: coords?.lng || null,
      });
      setLeadSent(true);
      toast.success("Thanks — our team will contact you.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLeadBusy(false);
    }
  };

  const chooseSavedAddress = (addressId) => {
    setSelectedAddress(addressId);
    const savedAddress = savedAddresses.find((item) => item.id === addressId);
    if (savedAddress) {
      setAddress(savedAddress.address);
      setPhone(savedAddress.phone);
      if (usableCoordinate(savedAddress.latitude) && usableCoordinate(savedAddress.longitude)) {
        setCoords({ lat: savedAddress.latitude, lng: savedAddress.longitude });
      }
    }
  };

  const whatsappRequest =
    "https://wa.me/917980016662?text=" +
    encodeURIComponent(`Please start Quick Door Now delivery service in my area: ${address}`);

  const payOptions = [
    { key: "COD", label: "Cash on Delivery", desc: "Pay when you receive", icon: Banknote },
    {
      key: "WALLET",
      label: "Quick Door Wallet",
      desc: `Balance ${formatINR(walletBalance)}`,
      icon: WalletIcon,
      disabled: walletShort,
    },
    { key: "ONLINE", label: "UPI Payment", desc: "UPI Intent / QR scan", icon: Smartphone },
  ];

  return (
    <main className="mx-auto max-w-5xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <h1 className="font-serif text-4xl tracking-tight">Checkout</h1>
      <p className="text-sm text-stone-500">Confirm your details to complete order.</p>
      <div className="mt-5">
        <StoreClosedNotice status={info.store_status} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        <form onSubmit={submit} className="space-y-4">
          <section className="rounded-2xl border border-stone-200 bg-white p-5">
            <h2 className="font-display text-lg font-semibold">Delivery Address</h2>
            {savedAddresses.length > 0 && (
              <div className="mt-4" data-testid="checkout-saved-addresses">
                <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Saved addresses</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {savedAddresses.map((savedAddress) => (
                    <button
                      type="button"
                      key={savedAddress.id}
                      onClick={() => chooseSavedAddress(savedAddress.id)}
                      data-testid={`checkout-address-option-${savedAddress.id}`}
                      className={`min-h-16 rounded-xl border p-3 text-left text-xs transition-colors ${
                        selectedAddress === savedAddress.id
                          ? "border-orange-600 bg-orange-50 text-stone-950"
                          : "border-stone-200 hover:border-stone-300"
                      }`}
                    >
                      <span className="block font-semibold">{savedAddress.label}</span>
                      <span className="mt-1 block line-clamp-2 text-stone-500">{savedAddress.address}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => nav("/select-location")}
              data-testid="checkout-open-map"
              className="mt-4 flex w-full items-center gap-3 rounded-xl border border-dashed border-orange-300 bg-orange-50 p-3 text-left transition-colors hover:border-orange-500"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#FF5200] text-white">
                <MapPin size={18} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-stone-900">Change Location / Pin on Map</span>
                <span className="mt-0.5 block truncate text-xs text-stone-500">
                  {location?.address || "Tap to open the full-screen map picker"}
                </span>
              </span>
            </button>

            <textarea
              data-testid="checkout-address"
              required
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={3}
              placeholder="House no, street, landmark, Mirganj..."
              className="mt-3 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-stone-700"
            />
            {isOutOfServiceArea && (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950" data-testid="checkout-service-area-warning">
                {deliveryPin ? `PIN ${deliveryPin}` : "This map pin"} is outside our active Mirganj delivery range.
              </div>
            )}
            {isInServiceArea && (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="checkout-service-area-confirmed">
                Great — your location is in our active Mirganj delivery range.
              </div>
            )}
            <input
              data-testid="checkout-phone"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile number"
              className="mt-3 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-stone-700"
            />
            <input
              data-testid="checkout-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Delivery notes (optional)"
              className="mt-3 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-stone-700"
            />
          </section>

          <section className="rounded-2xl border border-stone-200 bg-white p-5">
            <h2 className="font-display text-lg font-semibold">Payment</h2>
            <div className="mt-3 space-y-2">
              {payOptions.map(({ key, label, desc, icon: Icon, disabled }) => (
                <button
                  type="button"
                  key={key}
                  disabled={disabled}
                  data-testid={`pay-${key.toLowerCase()}`}
                  onClick={() => setPay(key)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left text-sm transition-colors ${
                    pay === key
                      ? "border-orange-600 bg-orange-50 text-stone-900"
                      : "border-stone-200 hover:border-stone-300"
                  } ${disabled ? "opacity-50" : ""}`}
                >
                  <Icon size={20} className={pay === key ? "text-[#FF5200]" : "text-stone-500"} />
                  <span className="flex-1">
                    <span className="block font-semibold">{label}</span>
                    <span className="block text-xs text-stone-500">
                      {desc}
                      {key === "WALLET" && disabled ? " · low balance" : ""}
                    </span>
                  </span>
                  <span className={`grid h-5 w-5 place-items-center rounded-full border-2 ${pay === key ? "border-[#FF5200]" : "border-stone-300"}`}>
                    {pay === key && <span className="h-2.5 w-2.5 rounded-full bg-[#FF5200]" />}
                  </span>
                </button>
              ))}
            </div>
            {pay === "WALLET" && walletShort && (
              <button
                type="button"
                onClick={() => nav("/wallet")}
                data-testid="checkout-add-money"
                className="mt-3 text-xs font-bold text-[#FF5200] underline"
              >
                Add money to wallet →
              </button>
            )}
          </section>

          <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="checkout-tip">
            <h2 className="font-display text-lg font-semibold">Add a tip for your rider</h2>
            <p className="text-xs text-stone-500">100% of the tip goes to your delivery partner.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[10, 20, 30, 50].map((amt) => (
                <button
                  type="button"
                  key={amt}
                  data-testid={`tip-${amt}`}
                  onClick={() => {
                    setTip(tip === amt ? 0 : amt);
                    setCustomTip("");
                  }}
                  className={`min-h-10 rounded-full border px-4 text-sm font-semibold transition-colors ${
                    tip === amt ? "border-orange-600 bg-orange-50 text-orange-700" : "border-stone-300 text-stone-700 hover:border-orange-300"
                  }`}
                >
                  ₹{amt}
                </button>
              ))}
              <input
                data-testid="tip-custom"
                type="number"
                min="0"
                value={customTip}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomTip(v);
                  setTip(Math.max(0, Number(v) || 0));
                }}
                placeholder="Custom ₹"
                className="min-h-10 w-28 rounded-full border border-stone-300 px-4 text-sm outline-none focus:border-orange-400"
              />
            </div>
          </section>

          <button
            data-testid="place-order-btn"
            disabled={busy || items.length === 0 || storeClosed}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FF5200] py-3 text-sm font-bold text-white hover:bg-[#FF6A00] disabled:opacity-50"
          >
            {busy ? "Processing..." : pay === "COD" ? `Place Order · ${formatINR(total)}` : `Proceed to Pay · ${formatINR(total)}`}
          </button>
        </form>

        <aside className="h-fit rounded-2xl border border-stone-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Order Summary</h2>
          <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
            {items.map((i) => (
              <div key={i.line_id} className="flex items-center gap-3 text-sm">
                <ProductImage src={i.image} alt={i.name} className="h-10 w-10 rounded-lg object-cover" testId={`checkout-product-image-${i.product_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate">{i.name}</p>
                  <p className="text-xs text-stone-500">{i.variant_label ? `${i.variant_label} · ` : ""}Qty {i.qty}</p>
                </div>
                <p className="font-medium">{formatINR(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 border-t border-stone-200 pt-3 text-sm">
            <div className="flex gap-2">
              <input
                data-testid="coupon-input"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Have a coupon? e.g. MIRGANJ50"
                className="flex-1 rounded-lg border border-stone-200 px-2 py-1 text-xs outline-none"
              />
              <button type="button" data-testid="coupon-apply" onClick={applyCoupon} className="rounded-lg bg-stone-900 px-3 py-1 text-xs font-semibold text-white">
                Apply
              </button>
            </div>
            <div className="flex justify-between text-stone-600"><span>Subtotal</span> <span>{formatINR(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-green-700"><span>Coupon {coupon?.code}</span> <span>-{formatINR(discount)}</span></div>
            )}
            <div className="flex justify-between text-stone-600"><span>Delivery</span> <span>{deliveryFee === 0 ? "FREE" : formatINR(deliveryFee)}</span></div>
            {Number(tip) > 0 && (
              <div className="flex justify-between text-stone-600" data-testid="summary-tip"><span>Rider tip</span> <span>{formatINR(Number(tip))}</span></div>
            )}
            <div className="flex justify-between border-t border-stone-200 pt-2 text-base font-semibold text-stone-900"><span>Total</span> <span>{formatINR(total)}</span></div>
          </div>
        </aside>
      </div>

      {showOutOfZoneModal && (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/45 p-3 sm:place-items-center" data-testid="out-of-zone-modal">
          <section className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h2 className="text-xl font-extrabold text-slate-950">We are expanding to your area soon!</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Quick Door Now is not yet delivering to your exact location.
            </p>
            <a
              href={whatsappRequest}
              target="_blank"
              rel="noreferrer"
              data-testid="out-of-zone-whatsapp"
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-xl bg-[#25D366] px-4 text-sm font-extrabold text-white"
            >
              Request Quick Door Now in My Area
            </a>
            <button
              type="button"
              onClick={() => setShowOutOfZoneModal(false)}
              data-testid="out-of-zone-change-address"
              className="mt-2 flex min-h-11 w-full items-center justify-center rounded-xl border border-slate-300 text-sm font-bold text-slate-700"
            >
              Change to a Mirganj Delivery Address
            </button>
            {!leadSent ? (
              <form onSubmit={saveUnserviceableLead} className="mt-4 space-y-2 border-t border-slate-100 pt-4">
                <input
                  required
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  data-testid="out-of-zone-lead-phone"
                  placeholder="Phone number"
                  className="min-h-11 w-full rounded-lg border border-slate-200 px-3 text-sm"
                />
                <button disabled={leadBusy} data-testid="submit-out-of-zone-lead" className="min-h-11 w-full rounded-lg bg-slate-900 text-sm font-extrabold text-white">
                  {leadBusy ? "Saving…" : "Notify me when you launch here"}
                </button>
              </form>
            ) : (
              <p className="mt-4 rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800" data-testid="out-of-zone-lead-saved">
                Your request is saved. We will be in touch.
              </p>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
